import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, User, Phone, Mail, MessageSquare, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: intro, 2: form, 3: success
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    query_type: 'general',
    message: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/whatsapp/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/whatsapp/lead', {
        ...formData,
        source_page: window.location.pathname
      });
      
      if (response.data.whatsapp_link) {
        setStep(3);
        // Open WhatsApp after a brief delay
        setTimeout(() => {
          window.open(response.data.whatsapp_link, '_blank');
        }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetWidget = () => {
    setStep(1);
    setFormData({
      name: '',
      phone: '',
      email: '',
      query_type: 'general',
      message: ''
    });
  };

  if (!settings?.enabled || !settings?.has_phone) {
    return null;
  }

  return (
    <>
      {/* Floating WhatsApp Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="floating-whatsapp"
        data-testid="whatsapp-widget-btn"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </button>

      {/* Widget Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[1001] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div 
            className="w-full max-w-md bg-background border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
            data-testid="whatsapp-widget-modal"
          >
            {/* Header */}
            <div className="bg-[#25D366] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">{settings?.business_name || 'WhatsApp Support'}</h3>
                  <p className="text-white/80 text-sm">{settings?.working_hours || 'We typically reply within minutes'}</p>
                </div>
              </div>
              <button 
                onClick={() => { setIsOpen(false); resetWidget(); }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {step === 1 && (
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 mx-auto bg-[#25D366]/10 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-10 h-10 text-[#25D366]" />
                  </div>
                  <h4 className="text-xl font-bold">Chat with us on WhatsApp!</h4>
                  <p className="text-muted-foreground">
                    Get instant support. Fill in your details and we'll connect you directly.
                  </p>
                  <button
                    onClick={() => setStep(2)}
                    className="w-full py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                    data-testid="start-chat-btn"
                  >
                    Start Chat <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {step === 2 && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Your Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Enter your name"
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/50"
                        data-testid="wa-name-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+91 98765 43210"
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/50"
                        data-testid="wa-phone-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">Email (Optional)</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your@email.com"
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/50"
                        data-testid="wa-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">How can we help?</label>
                    <select
                      value={formData.query_type}
                      onChange={(e) => setFormData({...formData, query_type: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-[#25D366]"
                      data-testid="wa-query-type"
                    >
                      {settings?.query_types?.map((qt) => (
                        <option key={qt.id} value={qt.id}>{qt.label}</option>
                      )) || (
                        <>
                          <option value="general">General Inquiry</option>
                          <option value="support">Customer Support</option>
                          <option value="sales">Sales Query</option>
                          <option value="feedback">Feedback</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5">Message (Optional)</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Tell us more about your query..."
                        rows={3}
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/50 resize-none"
                        data-testid="wa-message-input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="wa-submit-btn"
                  >
                    {loading ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Continue to WhatsApp
                      </>
                    )}
                  </button>
                </form>
              )}

              {step === 3 && (
                <div className="text-center space-y-4 py-4">
                  <div className="w-20 h-20 mx-auto bg-green-500/10 rounded-full flex items-center justify-center animate-bounce">
                    <MessageCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h4 className="text-xl font-bold text-green-500">Opening WhatsApp...</h4>
                  <p className="text-muted-foreground">
                    You'll be redirected to WhatsApp with your message pre-filled. If it doesn't open automatically, click below.
                  </p>
                  <button
                    onClick={() => {
                      api.post('/whatsapp/lead', {
                        ...formData,
                        source_page: window.location.pathname
                      }).then(res => {
                        if (res.data.whatsapp_link) {
                          window.open(res.data.whatsapp_link, '_blank');
                        }
                      });
                    }}
                    className="text-[#25D366] hover:underline font-medium"
                  >
                    Click here to open WhatsApp
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default WhatsAppWidget;
